/* eslint-disable react-hooks/exhaustive-deps */
import { showAddOrUpdateServiceState } from "@/atoms";
import axios from "@/axios.config.ts";
import CustomInput from "@/components/common/inputs/CustomInput.tsx";
import MainModal from "@/components/modals/MainModal.tsx";
import useAuth from "@/hooks/useAuth";
import { useGetAllCouncils } from "@/services/councils";
import { useCreateService, useGetServicesByCouncil, useUpdateService } from "@/services/services";
import { EServiceStatus } from "@/types";
import { Button, MultiSelect, Select } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";


const AddUpdateService = () => {
  const [show, setShow] = useRecoilState(showAddOrUpdateServiceState);

  const { user } = useAuth();
  const [data, setData] = useState({
    name: "",
    registrationFee: 0,
    council_id: null as string | null,
    approvalFinalLevel: 0,
    code: "",
    status: EServiceStatus.ACTIVE,
    dependentServiceIds: [] as string[],
  });
  const [roleApproval, setRoleApproval] = useState<any[]>([]);
  const {
    mutate: createService,
    isPending: creatingService
  } = useCreateService();
  const {
    mutate: updateService,
    isPending: updatingService
  } = useUpdateService();

  const queryClient = useQueryClient();

  const {
    data: servicesByCouncil,
  } = useGetServicesByCouncil({
    councilId: data.council_id || undefined,
    enabled: !!data.council_id,
  });
  const {
    data: tblcouncils,
  } = useGetAllCouncils();

  const fetchLevels = async (council_id: string) => {
    try {
      const response = await axios.get(
        `/approvalstructurelevel/council/${council_id}`
      );
      if (Array.isArray(response.data)) {
        const formattedData = response.data.map((item: any) => ({
          value: String(item.approvalLevel),
          label: `${item.role.roleName} - Level ${item.approvalLevel}`,
        }));
        setRoleApproval(formattedData);
      } else {
        setRoleApproval([]);
      }
    } catch (error) {
      console.error("Error fetching roles levels:", error);
      setRoleApproval([]);
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (show?.action === "add") {
      createService({
        ...data,
        council_id: data.council_id!,
      }, {
        onSuccess: () => {
          notifications.show({
            title: "Success",
            message: "Service Added Successfully",
            color: "green",
          });
          if (user?.roles[0].roleName === "ADMINCOUNCIL") {
            queryClient.invalidateQueries({
              queryKey: ["services", user?.usercouncil[0]?.id],
            })
          } else {
            queryClient.invalidateQueries({
              queryKey: ["services"],
            })
          }
          setShow(null);
        },
        onError: (error) => {
          notifications.show({
            title: "Error",
            message: error.response?.data?.message || "Failed to add service",
            color: "red",
          });
        }
      })
    } else if (show?.action === "edit" && show.service) {
      updateService({
        ...show.service,
        ...data,
        council_id: data.council_id || show.service.council_id,
      }, {
        onSuccess: () => {
          notifications.show({
            title: "Success",
            message: "Service Updated Successfully",
            color: "green",
          });
          if (user?.roles[0].roleName === "ADMINCOUNCIL") {
            queryClient.invalidateQueries({
              queryKey: ["services", user?.usercouncil[0]?.id],
            })
          } else {
            queryClient.invalidateQueries({
              queryKey: ["services"],
            })
          }
          setShow(null);
        },
        onError: (error) => {
          notifications.show({
            title: "Error",
            message: error.response?.data?.message || "Failed to update service",
            color: "red",
          });
        }
      })
    }
  };


  useEffect(() => {
    if (data.council_id) {
      fetchLevels(String(data.council_id));
    }
  }, [data.council_id]);

  useEffect(() => {
    if (user?.roles[0].roleName === "ADMINCOUNCIL") {
      setData({
        ...data,
        council_id: user?.usercouncil[0]?.id,
      });
    }
  }, [user]);

  useEffect(() => {
    if (show?.show && show.action === "edit" && show.service) {
      setData({
        name: show.service.name,
        registrationFee: show.service.registrationFee,
        council_id: show.service.council_id,
        approvalFinalLevel: show.service.approvalFinalLevel,
        code: show.service.code,
        status: show.service.status,
        dependentServiceIds: show.service.dependentServices.map((item) => item.id),
      });
    }
  }, [show])

  return (
    <MainModal open={Boolean(show?.show)} onClose={() => setShow(null)} title={show?.action === "add" ? "Add Service" : "Update Service"}>
      <form onSubmit={onSubmit} className="flex flex-col gap-y-2">
        <CustomInput
          type="text"
          name="name"
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          label="Name"
          placeholder="Name"
          alwaysFocused
        />
        <CustomInput
          type="text"
          name="code"
          value={data.code}
          onChange={(e) => setData({ ...data, code: e.target.value })}
          label="Enter Code"
          placeholder="Please Choose valid Code"
          alwaysFocused
        />
        {
          user?.roles[0].roleName === "ADMIN" &&
          <CustomInput label="Select Your Council" alwaysFocused>
            <Select
              value={data.council_id ?? ""}
              onChange={(val) => setData({ ...data, council_id: val ?? null })}
              data={tblcouncils?.data.map((item) => ({
                value: item.id,
                label: item.councilName,
              }))}
              placeholder="Select Your Council"
              variant="unstyled"
              width="100%"
              className="w-full"
            />
          </CustomInput>
        }
        <CustomInput label="Select Final Structure Level" alwaysFocused>
          <Select
            value={String(data.approvalFinalLevel)}
            onChange={(val) =>
              setData({ ...data, approvalFinalLevel: Number(val) })
            }
            data={roleApproval.map((item) => ({
              value: item.value,
              label: item.label,
            }))}
            placeholder="Select Final Structure Level"
            variant="unstyled"
            width="100%"
            className="w-full"
            disabled={!data.council_id}
          />
        </CustomInput>
        <CustomInput
          type="number"
          name="amount"
          value={data.registrationFee}
          onChange={(e) => setData({ ...data, registrationFee: Number(e.target.value) })}
          label="Amount"
          placeholder="Enter Amount"
          alwaysFocused
        />
        <CustomInput label="Select Dependent Services" alwaysFocused>
          <MultiSelect
            value={data.dependentServiceIds}
            onChange={(val) =>
              setData({ ...data, dependentServiceIds: val ?? [] })
            }
            data={servicesByCouncil?.data?.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            placeholder="Select Dependent Services"
            variant="unstyled"
            width="100%"
            className="w-full"
            multiple
          />
        </CustomInput>
        <Button
          loading={creatingService || updatingService}
          disabled={
            creatingService ||
            updatingService ||
            !data.name ||
            !data.council_id ||
            !data.approvalFinalLevel ||
            !data.registrationFee
          }
          type="submit"
          className="mt-4"
          size="lg"
        >
          {show?.action === "add" ? "Add Service" : "Update Service"}
        </Button>
      </form>
    </MainModal>
  );
};

export default AddUpdateService;
